import React, { FC, useCallback } from "react";
import {
    amountInDecimal,
    Bold,
    CancelNextButton,
    FinishPageType,
    FlowPage,
    FlowPageProps,
    Highlight,
    intervalTOKey,
    NormalText,
    SmallText,
} from "../cancelFlow";
import "./pages.css";
import { IonButton, IonIcon, IonText } from "@ionic/react";
import { checkmarkCircle, closeCircle } from "ionicons/icons";
import proBadge from "../../../../../assets/images/pro-badge.png";
import plusBadge from "../../../../../assets/images/upgrade-plus-and-pro/plus-badge.png";
import {
    useCurrentStripePlan,
    useStripeSubscription,
    useStripeUpgradePlan,
} from "../../../../../components/hooks";
import { getLocale } from "../../../../../utils/localeUtils";
import { formatForCurrency } from "../../../../../utils/CurrencySymbol";
import * as stripeService from "../../../../../services/StripeService";
import {
    asPlanType,
    planDisplayName,
} from "../../../../../services/StripeService";
import { Trans, useTranslation } from "react-i18next";
import { trackEvent } from "../../../../../services/analytics/analyticsService";
import { ProductBadge } from "../../../../../components/ProductBadge";

const IconPlus = () => {
    return <img src={plusBadge} />;
};
const IconPro = () => {
    return <img src={proBadge} />;
};
const EnabledIcon = () => {
    return (
        <IonIcon
            icon={checkmarkCircle}
            className={"enabled"}
            size={"large"}
        ></IonIcon>
    );
};
const DisabledIcon = () => {
    return (
        <IonIcon
            icon={closeCircle}
            className={"disabled"}
            size={"large"}
        ></IonIcon>
    );
};
const Feature: FC<{ name: string; plus: boolean }> = ({ name, plus }) => {
    return (
        <div className={"feature"}>
            <div className={"name"}>{name}</div>
            <div className={"plus"}>
                {plus ? <EnabledIcon /> : <DisabledIcon />}
            </div>
            <div className={"pro"}>
                <EnabledIcon />
            </div>
        </div>
    );
};

export const OfferSwitchPage = (interval: "month" | "year") => {
    const OfferSwitchPageInternal: FC<FlowPageProps> = ({
        next,
        finish,
        reason,
    }) => {
        const sub = useStripeSubscription();
        const { t } = useTranslation();
        const currentPlan = useCurrentStripePlan();
        const type = sub?.planFeatures || "";
        // If our current plan is basic, then we are upgrading to pro
        const isUpgrade = asPlanType(type) === "basic";
        const upgradePlanName = isUpgrade ? "pro" : "basic";
        const upgradePlan = useStripeUpgradePlan(
            interval,
            asPlanType(upgradePlanName),
            true
        );
        const locale = getLocale();
        const currency = currentPlan?.currency || "USD";
        const currentPlanAmount = amountInDecimal(
            currentPlan?.amount * 12,
            currency
        );
        const upgradePlanAmount = amountInDecimal(
            upgradePlan?.amount,
            currency
        );
        const price = formatForCurrency(upgradePlanAmount, currency, locale);
        const onClickUpgrade = useCallback(
            () =>
                finish &&
                finish(async () => {
                    // Handle the upgrade logic here
                    try {
                        await stripeService.updatePlan(
                            interval,
                            asPlanType(upgradePlanName),
                            isUpgrade
                        );
                        trackEvent("CancellationOutcome", {
                            reason: reason,
                            outcome: isUpgrade
                                ? "switched-to-pro"
                                : "switched-to-basic",
                        });
                    } catch (error) {
                        console.error("Error upgrading plan:", error);
                    }
                    return isUpgrade
                        ? FinishPageType.OfferSwitchInstant
                        : FinishPageType.OfferSwitch;
                }),
            [sub, finish]
        );
        return (
            <FlowPage className={"offer-switch-page"}>
                <ProductBadge
                    type={asPlanType(upgradePlanName)}
                    height={"30px"}
                />
                <div className={"content"}>
                    {isUpgrade ? (
                        <>
                            <NormalText>
                                <Trans
                                    i18nKey={
                                        "cancellation.screens.offerSwitch.upgrade.text"
                                    }
                                    values={{
                                        newPlan: planDisplayName(upgradePlan),
                                        newPrice: price,
                                        interval: `${t(
                                            intervalTOKey(interval)
                                        )}*`,
                                    }}
                                    components={{
                                        h: <Highlight />,
                                        b: <Bold />,
                                    }}
                                />
                            </NormalText>
                            <SmallText>
                                {t(
                                    "cancellation.screens.offerSwitch.upgrade.prorated"
                                )}
                            </SmallText>
                        </>
                    ) : (
                        <NormalText>
                            <Trans
                                i18nKey={
                                    "cancellation.screens.offerSwitch.downgrade.text"
                                }
                                values={{
                                    newPlan: planDisplayName(upgradePlan),
                                    newPrice: price,
                                    interval: t(
                                        intervalTOKey(interval)
                                    ).toLowerCase(),
                                }}
                                components={{ h: <Highlight />, b: <Bold /> }}
                            />
                        </NormalText>
                    )}
                    <div className={"table"}>
                        <div className={"header"}>
                            <div className={"name"}></div>
                            <div className={"plus"}>
                                <IconPlus />
                            </div>
                            <div className={"pro"}>
                                <IconPro />
                            </div>
                        </div>
                        <Feature
                            name={t("upgrade.goProModal.heatmaps")}
                            plus={false}
                        />
                        <Feature
                            name={t("upgrade.goProModal.performanceData")}
                            plus={false}
                        />
                        <Feature
                            name={t("upgrade.goProModal.videoAnalysis")}
                            plus={false}
                        />
                        <Feature name={t("general.trends")} plus={false} />
                        <Feature
                            name={t("upgrade.goProModal.advancedTimers")}
                            plus={true}
                        />
                        <Feature
                            name={t("upgrade.basicFeatures.calendarSync")}
                            plus={true}
                        />

                        <Feature
                            name={t("upgrade.basicFeatures.addFreeExperience")}
                            plus={true}
                        />
                        <Feature
                            name={t("upgrade.basicFeatures.uploadTool")}
                            plus={true}
                        />
                        <Feature
                            name={t("upgrade.proFeaturesContent.tableFeature6")}
                            plus={true}
                        />
                    </div>
                </div>
                <CancelNextButton next={next} />
                <IonButton
                    fill={"solid"}
                    onClick={onClickUpgrade}
                    data-testid="offer-switch-button"
                >
                    <IonText>
                        {isUpgrade
                            ? t(
                                  "cancellation.screens.offerSwitch.upgrade.button",
                                  {
                                      newPlan: planDisplayName(upgradePlan),
                                      newPrice: price,
                                      interval: `${t(
                                          intervalTOKey(interval)
                                      ).toLowerCase()}*`,
                                  }
                              )
                            : t(
                                  "cancellation.screens.offerSwitch.downgrade.button",
                                  {
                                      newPlan: planDisplayName(upgradePlan),
                                      newPrice: price,
                                      interval: t(
                                          intervalTOKey(interval)
                                      ).toLowerCase(),
                                  }
                              )}
                    </IonText>
                </IonButton>
            </FlowPage>
        );
    };
    return OfferSwitchPageInternal;
};
